import { Box, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dlv from 'dlv';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { AuthData } from '../../context/auth';
import { FORM_MESSAGE } from './data/constants';
import { FormBuilder, FormShape, SaveFastForm } from './types';
import Form from './utils/getForm';

interface FastFormProps<T> {
	/**
	 * The form to render from the FormShape<FormBuilder> object
	 * @type {string}
	 * @memberof FastFormProps
	 */
	formName: string;

	/**
	 * The top level FormShape<FormBuilder> object
	 * @type {FormShape<FormBuilder>}
	 * @memberof FastFormProps
	 */
	form: FormShape<FormBuilder>;

	/**
	 * Auth data from context
	 * @type {AuthData}
	 * @memberof FastFormProps
	 */
	authData: AuthData;

	/**
	 * Product Name from context
	 * @type {string}
	 * @memberof FastFormProps
	 */
	productName: string;

	/**
	 * Config version from context
	 * @type {string}
	 * @memberof FastFormProps
	 */
	configVersion: string;

	/**
	 * Optional, if the form is disabled (or locked)
	 * @type {boolean}
	 * @memberof FastFormProps
	 */
	disabled?: boolean;

	/**
	 * Optional, if the form disables external calls
	 * @type {boolean}
	 * @memberof FastFormProps
	 */
	disableExternalCalls?: boolean;

	effectiveDate?: string;
	/**
	 * Optional, the save function for the form
	 * @type {SaveFastForm}
	 * @memberof FastFormProps
	 */
	save?: SaveFastForm<T>;
	setLoading?: (loading: boolean) => void;
}

/**
 *
 * Forms for FEv2 from Config and Policy data
 * @param {FastFormProps} {
 * 	form,
 * 	formName,
 * 	authData,
 * 	configVersion,
 * 	disabled,
 * 	save,
 * }
 * @return {*}
 */
const FastForm = <T,>({
	form,
	formName,
	authData,
	productName,
	configVersion,
	disabled,
	disableExternalCalls,
	effectiveDate,
	save,
	setLoading,
}: FastFormProps<T>) => {
	const methods = useFormContext();

	if (!methods) {
		console.error(
			'FastForm: No FormProvider found. Please wrap your form in a FormProvider with methods from useFastForm',
		);
		return null;
	}

	const { getValues, formState, reset } = methods;
	const { dirtyFields } = formState;

	const currForm = dlv(form, formName, null) as FormShape<FormBuilder>;
	const externalDataCalls = disableExternalCalls
		? []
		: currForm?.externalDataCalls || [];
	const fields = currForm?.fields || [];

	if (fields.length === 0) {
		// Display message on any form when no fields are configured
		return (
			<Box sx={{ margin: 2 }}>
				<Typography variant='subtitle2' color='textSecondary'>
					<em>{FORM_MESSAGE.noFields}</em>
				</Typography>
			</Box>
		);
	}

	const saveForm = async () => {
		await save(getValues(), dirtyFields, reset);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			{Form(
				formName,
				fields,
				externalDataCalls,
				methods.control,
				authData,
				productName,
				configVersion,
				methods.getValues(),
				null,
				disabled,
				effectiveDate,
				saveForm,
				setLoading,
			)}
		</LocalizationProvider>
	);
};

export default FastForm;
